<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="book"
      :badge="subjects.length ? `${subjects.length} subjects` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Subject
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredSubjects"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search subjects..."
      empty-icon="book"
      empty-title="No subjects found"
      empty-description="Add your first subject to get started"
    >
      <template #filters>
        <select v-model="filterStatus" class="filter-select-modern">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </template>

      <template #cell-order="{ item, value }">
        <span class="order-badge">{{ item.orderIndex || item.order_index || value }}</span>
      </template>

      <template #cell-subject="{ item }">
        <div class="avatar-info">
          <router-link :to="`/app/subjects/${item.id}/topics`" class="avatar-name text-primary">
            {{ item.name }}
          </router-link>
          <span v-if="item.description" class="avatar-subtitle">{{ truncate(item.description, 50) }}</span>
        </div>
      </template>

      <template #cell-topics="{ item }">
        <router-link :to="`/app/subjects/${item.id}/topics`" class="badge bg-info text-decoration-none">
          {{ item.topicCount || 0 }} topics
        </router-link>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', (item.isActive ?? item.is_active) !== false ? 'status-active' : 'status-inactive']">
          {{ (item.isActive ?? item.is_active) !== false ? 'active' : 'inactive' }}
        </span>
      </template>

      <template #cell-created="{ item }">
        <span class="text-muted">{{ formatDate(item.createdAt) }}</span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <router-link 
            :to="`/app/subjects/${item.id}/topics`" 
            class="action-btn action-view" 
            v-tooltip="'View topics'"
          >
            <vue-feather type="list" size="14" />
          </router-link>
          <button 
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit subject'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete subject', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Add First Subject
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Subject' : 'Add Subject'"
      icon="book"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.name"
      :item-id="isEditing ? editingId : ''"
      @submit="saveSubject"
    >
      <form @submit.prevent="saveSubject" class="modal-form">
        <div class="form-group">
          <label class="form-label">Subject Name <span class="text-danger">*</span></label>
          <input 
            v-model="form.name" 
            type="text" 
            class="form-control" 
            placeholder="e.g., Mathematics, Physics"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="3"
            placeholder="Brief description of the subject..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Order</label>
            <input 
              v-model.number="form.order_index" 
              type="number" 
              class="form-control" 
              placeholder="1"
              min="0"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Active</label>
            <select v-model="form.is_active" class="form-control">
              <option :value="true">Active</option>
              <option :value="false">Inactive</option>
            </select>
          </div>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

export default {
  name: 'SubjectsList',
  components: { DataTable, CreateModal },
  setup() {
    const route = useRoute()
    const alert = useAlert()
    const examId = route.params.examId

    const columns = [
      { key: 'order', label: '#', width: '60px' },
      { key: 'subject', label: 'Subject Name', sortable: true, minWidth: '280px' },
      { key: 'topics', label: 'Topics', class: 'text-center' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'created', label: 'Created', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const exam = ref(null)
    const subjects = ref([])
    const loading = ref(true)
    const filterStatus = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      name: '',
      description: '',
      order_index: 1,
      is_active: true
    })

    const breadcrumbs = computed(() => [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', url: '/app/exams/list' },
      { title: exam.value?.name || 'Exam', active: true }
    ])

    const filteredSubjects = computed(() => {
      if (!filterStatus.value) return subjects.value
      if (filterStatus.value === 'active') return subjects.value.filter(s => (s.isActive ?? s.is_active) !== false)
      return subjects.value.filter(s => (s.isActive ?? s.is_active) === false)
    })

    const fetchData = async () => {
      loading.value = true
      try {
        const [examRes, subjectsRes] = await Promise.all([
          api.get(`/v1/admin/exams/${examId}`),
          api.get(`/v1/admin/exams/${examId}/subjects`)
        ])
        exam.value = examRes.data.exam
        subjects.value = subjectsRes.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch data:', err)
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.name = ''
      form.description = ''
      form.order_index = subjects.value.length + 1
      form.is_active = true
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (subject) => {
      isEditing.value = true
      editingId.value = subject.id
      form.name = subject.name || ''
      form.description = subject.description || ''
      form.order_index = subject.orderIndex ?? subject.order_index ?? subject.order ?? 1
      form.is_active = subject.isActive ?? subject.is_active ?? true
      showModal.value = true
    }

    const saveSubject = async () => {
      if (!form.name) return
      
      saving.value = true
      try {
        if (isEditing.value) {
          const response = await api.put(`/v1/admin/subjects/${editingId.value}`, form)
          const index = subjects.value.findIndex(s => s.id === editingId.value)
          if (index !== -1) {
            subjects.value[index] = { ...subjects.value[index], ...response.data.subject || form }
          }
          alert.success('Updated!', 'Subject updated successfully')
        } else {
          const payload = { ...form, exam_id: examId }
          const response = await api.post('/v1/admin/subjects', payload)
          subjects.value.push(response.data.subject || response.data)
          alert.success('Created!', 'Subject created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save subject')
      } finally {
        saving.value = false
      }
    }

    const truncate = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }

    const confirmDelete = async (subject) => {
      const result = await alert.delete(
        'Delete Subject?',
        `This will also delete all topics, lessons, and tests under "${subject.name}".`
      )

      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/subjects/${subject.id}`)
          subjects.value = subjects.value.filter(s => s.id !== subject.id)
          alert.success('Deleted!', 'Subject has been deleted.')
        } catch (err) {
          alert.error('Error', 'Failed to delete subject')
        }
      }
    }

    onMounted(fetchData)

    return {
      examId,
      columns,
      exam,
      subjects,
      loading,
      filterStatus,
      breadcrumbs,
      filteredSubjects,
      showModal,
      isEditing,
      saving,
      form,
      openCreateModal,
      openEditModal,
      saveSubject,
      truncate,
      formatDate,
      confirmDelete
    }
  }
}
</script>

<style scoped>
.order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #f1f5f9;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  color: #64748b;
}

:global(body.dark-mode) .order-badge {
  background: #334155;
  color: #94a3b8;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}
</style>
